export const AUTH_MAX_AGE = 3600 * 24 * 5; // 5 дней
const MAX_AGE = 365 * 24 * 60 * 60; // 1 год

/**
 * Конфигурация сессии Magento
 */
export const SESSION_CONFIG = {
  /** Режим предпросмотр */
  DRAFT: {
    cookie: '',
    header: 'DRAFT',
    maxAge: 0,
    getHeaderValue: (value: string) => value,
    defaultValue: 'false'
  },
  /** Код магазина */
  STORE_CODE: {
    cookie: 'store',
    header: 'Store',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    sameSite: 'lax',
    defaultValue: ''
  },
  /** Язык магазина */
  LOCALE: {
    cookie: 'locale',
    header: '',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Токен авторизации пользователя */
  AUTH_TOKEN: {
    cookie: 'auth_token',
    header: 'Authorization',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => `Bearer ${value}`,
    sameSite: 'lax',
    defaultValue: ''
  },
  /** Email клиента */
  CUSTOMER_EMAIL: {
    cookie: 'customer_email',
    header: 'X-Magento-Customer-Email',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** ID клиента в mindbox */
  CUSTOMER_ID: {
    cookie: 'customer_id',
    header: 'X-Magento-Customer-Id',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** ID клиента в mindbox */
  CUSTOMER_MINDBOX_ID: {
    cookie: 'customer_mindbox_id',
    header: 'X-Magento-Customer-Mindbox-Id',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Идентификатор корзины */
  CART_ID: {
    cookie: 'cart_id',
    header: 'X-Magento-Vary',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    sameSite: 'lax',
    defaultValue: ''
  },
  /** Последний выбранный метод доставки в checkout */
  CHECKOUT_LAST_SHIPPING_METHOD_KEY: {
    cookie: 'checkout_last_shipping_method_key',
    header: '',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Последний выбранный метод оплаты в checkout */
  CHECKOUT_LAST_PAYMENT_METHOD_CODE: {
    cookie: 'checkout_last_payment_method_code',
    header: '',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Последний выбранный адрес пользователя в checkout */
  CHECKOUT_LAST_ADDRESS_ID: {
    cookie: 'checkout_last_address_id',
    header: '',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Флаг последнего выбранного pickup-адреса в checkout */
  CHECKOUT_LAST_ADDRESS_IS_PICKUP: {
    cookie: 'checkout_last_address_is_pickup',
    header: '',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Номер заказа */
  ORDER_NUMBER: {
    cookie: 'order_number',
    header: 'X-Magento-Order-Number',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Токен заказа */
  ORDER_TOKEN: {
    cookie: 'order_token',
    header: 'X-Magento-Order-Token',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Идентификатор wishlist */
  WISHLIST_ID: {
    cookie: 'wishlist_id',
    header: 'X-Magento-Wishlist',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Идентификатор mindbox */
  MINDBOX_DEVICE_UUID: {
    cookie: 'mindboxDeviceUUID', // куку ставит window.mindbox('create');
    header: 'mindbox-device-uuid',
    maxAge: AUTH_MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Город */
  LOCATION_CITY: {
    cookie: 'location_city',
    header: 'X-Location-City',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => encodeURIComponent(value), // для корректной работы с кириллицей
    // eslint-disable-next-line i18n/no-russian-character
    defaultValue: 'Москва'
  },
  /** Индекс (пустой, если в DaData нет postal_code — без подстановки 101000) */
  LOCATION_POSTCODE: {
    cookie: 'location_postcode',
    header: 'X-Location-Postcode',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  },
  /** Код ФИАС Федеральная информационная адресная системы */
  LOCATION_FIAS: {
    cookie: 'location_fias',
    header: 'X-Location-Fias',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: '0c5b2444-70a0-4932-980c-b4dc0d3f02b5'
  },
  /** Регион */
  LOCATION_REGION: {
    cookie: 'location_region',
    header: 'X-Location-Region',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => encodeURIComponent(value), // для корректной работы с кириллицей,
    // eslint-disable-next-line i18n/no-russian-character
    defaultValue: 'Москва'
  },
  /** Город без региона */
  LOCATION_CITY_PURE: {
    cookie: 'location_city_pure',
    header: 'X-Location-City-Pure',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => encodeURIComponent(value), // для корректной работы с кириллицей,
    // eslint-disable-next-line i18n/no-russian-character
    defaultValue: 'Москва'
  },
  /** Географическая долгота города */
  LOCATION_CITY_GEO_LON: {
    cookie: 'location_city_geo_lon',
    header: 'X-Location-City-Geo-Lon',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: '37.620393'
  },
  /** Географическая широта города */
  LOCATION_CITY_GEO_LAT: {
    cookie: 'location_city_geo_lat',
    header: 'X-Location-City-Geo-Lat',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: '55.75396'
  },
  /** Согласие на условия продаж */
  AGREEMENT_SALES: {
    cookie: 'agreement_sales',
    header: 'X-Agreement-Sales',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: 'false'
  },
  /** Согласие на обработку персональных данных */
  AGREEMENT_PRIVACY: {
    cookie: 'agreement_privacy',
    header: 'X-Agreement-Privacy',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: 'false'
  },
  /** Идентификатор diginetica view */
  DIGINETICA_SESSION_ID: {
    cookie: 'session_id',
    header: 'X-Diginetica-Session-Id',
    maxAge: 30 * 60, // Пол часа согласно ТЗ
    getHeaderValue: (value: string) => value,
    defaultValue: '1'
  },
  /** Идентификатор diginetica user */
  DIGINETICA_USER_GUID: {
    cookie: 'user_guid',
    header: 'X-Diginetica-User-Guid',
    maxAge: MAX_AGE,
    getHeaderValue: (value: string) => value,
    defaultValue: ''
  }
} as const;

/**
 * Базовые опции для куков
 */
export const COOKIE_OPTIONS = {
  path: '/',
  secure: process.env.NODE_ENV === 'production',
  httpOnly: true,
  sameSite: 'lax'
} as const;

/**
 * Тип ключей объекта SESSION_CONFIG
 */
export type SessionConfigKey = keyof typeof SESSION_CONFIG;

export const SESSION_KEYS = Object.keys(SESSION_CONFIG) as SessionConfigKey[];
